import { Box, Collapse, Divider, FormControl, FormLabel, Grid, IconButton, MenuItem, Select, Stack, Switch, TextField, Tooltip, Typography } from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useState, useMemo, useEffect } from "react";
import { AddCircleOutline, ChatBubble, Edit, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { colors, alphaVar } from "src/components/ui/colors";
import { useScopeOfWork } from "../../context/ScopeOfWorkContext";
import { ScopeOfWorkStep } from "../../context/scope-of-work.types";
import { TaskNoteBubble } from "../areas/Tasks/TaskNoteBubble";
import { ServiceAliasField } from "@components/common/ServiceAliasField";
import { EFrequencyType, JOB_FREQUENCY_OPTIONS, getJobFrequencyLabel } from "@components/scope-of-work/helpers";

const monetaryAmountOptions = [
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
  { value: "unique", label: "One Time" },
  { value: "per_item", label: "Per Item" },
  { value: "per_square_foot", label: "Per Square Foot" },
  { value: "per_car_space", label: "Per Car Space" },
  { value: "per_floor", label: "Per Floor" },
];

const isFlatFeePricingType = (by?: string | null): boolean => {
  if (!by) return true;
  return ["monthly", "yearly", "unique"].includes(by.toLowerCase());
};

type Props = {
  job: any;
  jobAlias: string;
  setJobAlias: (val: string) => void;
  jobDescription: string;
  setJobDescription: (val: string) => void;
};

export function ScopeOfWorkSummary({ job, jobAlias, setJobAlias, jobDescription, setJobDescription }: Props) {
  const { state, dispatch } = useScopeOfWork();

  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isEditingJobAlias, setIsEditingJobAlias] = useState(false);
  const [isEditingFrequency, setIsEditingFrequency] = useState(false);
  const [useCustomServiceName, setUseCustomServiceName] = useState(Boolean(state.serviceNameOverride?.trim()));
  const [hoverTaskUid, setHoverTaskUid] = useState<string | null>(null);
  const [openNotePreviewUid, setOpenNotePreviewUid] = useState<string | null>(null);
  const [expandedAreaUid, setExpandedAreaUid] = useState<string | null>(null);

  const currentFrequencyType = state.frequencyType || job?.scopeOfWork?.frequencyType || EFrequencyType.WEEKLY;

  useEffect(() => {
    setUseCustomServiceName(Boolean(state.serviceNameOverride?.trim()));
  }, [state.serviceNameOverride]);

  const pricingBy = state.costOverride?.by || "unique";
  const pricingPrice = state.costOverride?.price ?? state.costOverride?.value ?? 0;
  const pricingUnits = state.costOverride?.units ?? 1;
  const pricingCurrency = state.costOverride?.currency || "USD";
  const showUnits = !isFlatFeePricingType(pricingBy);

  const totalNum = useMemo(() => {
    const price = Number(pricingPrice) || 0;
    if (showUnits) {
      const units = Number(pricingUnits) || 0;
      return price * units;
    }
    return price;
  }, [pricingPrice, pricingUnits, showUnits]);

  const money = (value: number) => {
    return value.toLocaleString("en-US", {
      style: "currency",
      currency: pricingCurrency,
    });
  };

  const editScopeStep = (step: ScopeOfWorkStep) => {
    dispatch({ type: "SET_STEP", payload: step });
  };

  const handlePricingTypeChange = (event: any) => {
    const nextBy = event.target.value;
    dispatch({
      type: "SET_COST_OVERRIDE",
      payload: {
        ...state.costOverride,
        by: nextBy,
        units: isFlatFeePricingType(nextBy) ? undefined : (state.costOverride?.units ?? 1),
      },
    });
  };

  const handlePricingPriceChange = (event: any) => {
    const rawValue = event.target.value;
    dispatch({
      type: "SET_COST_OVERRIDE",
      payload: {
        ...state.costOverride,
        price: rawValue === "" ? null : Number(rawValue),
        value: rawValue === "" ? null : Number(rawValue),
      },
    });
  };

  const handlePricingUnitsChange = (event: any) => {
    const rawValue = event.target.value;
    dispatch({
      type: "SET_COST_OVERRIDE",
      payload: {
        ...state.costOverride,
        units: rawValue === "" ? null : Number(rawValue),
      },
    });
  };

  const categoryName = state.category?.name || "-";
  const typeOfPlaceName = state.places?.name || "-";
  const originalServiceName = state.service?.name || "Service";
  const displayedAdvancedServiceName =
    useCustomServiceName && state.serviceNameOverride?.trim() ? state.serviceNameOverride.trim() : originalServiceName;

  const allAreas = [...(state.areas || []), ...(state.otherAreas || [])];
  const activeAreas = allAreas.filter((area) => {
    const quantity = (area?.squareFootages || []).reduce((acc, item) => acc + (Number(item?.quantity) || 0), 0);
    return quantity > 0;
  });

  const getAreaQuantity = (area: any) => {
    return (area?.squareFootages || []).reduce((acc: number, item: any) => {
      return acc + (Number(item?.quantity) || 0);
    }, 0);
  };

  const selectedObjects = state.objects || [];
  const specialRequests = state.specialRequests || [];

  const isResidentialCategory = categoryName.toLowerCase() === "residential";
  const shouldShowSpecialRequests = isResidentialCategory;

  const squareFeet = state.squareft || "-";
  const unitLabel = "Unit";

  return (
    <Grid container spacing={3} sx={{ p: 1, alignItems: "flex-start" }}>
      <Grid item xs={12} lg={8}>
        {/* Client Display (Read-only) */}
        {job?.client && (
          <Card sx={{ border: "none", mb: 2 }}>
            <EditableSectionHeader title="Client" disabled={true} />
            <Row
              label="Name"
              value={[job.client.firstName, job.client.lastName].filter(Boolean).join(" ") || job.client.alias || job.client.businessName || "-"}
            />
            <Row label="Type" value={job.client.type} />
            <Row label="Phone" value={job.client.phoneNumber} />
            <Row label="Email" value={job.client.email} textTransform="none" />
          </Card>
        )}

        {/* Job Overview */}
        <Card sx={{ border: "none", mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, fontSize: 16 }}>
            Job Overview
          </Typography>

          <ServiceNameOverviewRow
            value={jobAlias}
            isEditing={isEditingJobAlias}
            onEdit={() => setIsEditingJobAlias(true)}
            onCancel={() => setIsEditingJobAlias(false)}
            onChange={(value) => setJobAlias(value)}
          />

          <EditableInfoRow label="Job type" value="Advanced" disabled={true} />

          <FrequencyOverviewRow
            value={currentFrequencyType}
            isEditing={isEditingFrequency}
            onEdit={() => setIsEditingFrequency(true)}
            onCancel={() => setIsEditingFrequency(false)}
            onChange={(value) => {
              dispatch({ type: "SET_FREQUENCY_TYPE", payload: value });
            }}
          />

          <EditableInfoRow label="Status" value={job?.status} disabled={true} />

          <Box sx={{ mt: 1.5 }}>
            <DescriptionSection
              descriptionJob={jobDescription}
              isEditingDescription={isEditingDescription}
              setIsEditingDescription={setIsEditingDescription}
              onChangeDescription={(value) => setJobDescription(value)}
            />
          </Box>
        </Card>

        {/* Scope of Work summary cards */}
        <Card sx={{ border: "none", mb: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 16 }}>
              Scope of Work
            </Typography>
            <Typography sx={{ fontSize: 12, color: colors.gray?.[400] || "var(--fx-surface-texttertiary, #94A3B8)", mt: 0.25 }}>
              Review each work section and edit it directly.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
              gap: 1.5,
            }}
          >
            <ScopeSummaryCard
              title="Category"
              subtitle="Service category"
              value={categoryName}
              onEdit={() => editScopeStep(ScopeOfWorkStep.Categories)}
            />

            <ScopeSummaryCard
              title="Type of Place"
              subtitle={`${squareFeet || "-"} sq ft`}
              value={typeOfPlaceName}
              onEdit={() => editScopeStep(ScopeOfWorkStep.TypeOfPlaces)}
            />

            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: alphaVar(colors.orange.base, 0.035),
                border: `1px solid ${alphaVar(colors.orange.base, 0.1)}`,
                minWidth: 0,
                gridColumn: { xs: "1", sm: "span 2" },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.5 }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 800,
                      textTransform: "uppercase",
                      letterSpacing: 0.6,
                      color: colors.orange.base,
                      mb: 0.5,
                    }}
                  >
                    Service
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: colors.gray?.[800] || "var(--fx-surface-text, #0F172A)",
                      lineHeight: 1.35,
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                    }}
                  >
                    {displayedAdvancedServiceName}
                  </Typography>

                  {useCustomServiceName && state.serviceNameOverride?.trim() ? (
                    <Typography sx={{ fontSize: 11, color: colors.gray?.[500] || "var(--fx-surface-textsecondary, #64748B)", mt: 0.35 }}>
                      Original service: {originalServiceName}
                    </Typography>
                  ) : (
                    <Typography sx={{ fontSize: 11, color: colors.gray?.[400] || "var(--fx-surface-texttertiary, #94A3B8)", mt: 0.35 }}>Selected service type</Typography>
                  )}
                </Box>

                <Tooltip title="Change service type">
                  <IconButton
                    onClick={() => editScopeStep(ScopeOfWorkStep.TypeOfService)}
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1.25,
                      bgcolor: "var(--fx-surface-base, #FFFFFF)",
                      border: `1px solid ${alphaVar(colors.orange.base, 0.16)}`,
                      color: colors.orange.base,
                      flexShrink: 0,
                      "&:hover": { bgcolor: alphaVar(colors.orange.base, 0.1) },
                    }}
                  >
                    <Edit sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              </Box>

              <ServiceAliasField
                enabled={useCustomServiceName}
                value={state.serviceNameOverride || ""}
                originalServiceName={originalServiceName}
                size="small"
                onEnabledChange={setUseCustomServiceName}
                onValueChange={(value) => {
                  dispatch({
                    type: "SET_SERVICE_NAME_OVERRIDE",
                    payload: value,
                  });
                }}
              />
            </Box>

            <ScopeSummaryCard
              title="Areas & Tasks"
              subtitle="Selected work areas"
              value={`${activeAreas.length} areas`}
              onEdit={() => editScopeStep(ScopeOfWorkStep.Areas)}
            />

            <ScopeSummaryCard
              title="Objects"
              subtitle="Selected objects"
              value={`${selectedObjects.length} objects`}
              onEdit={() => editScopeStep(ScopeOfWorkStep.Objects)}
            />

            {shouldShowSpecialRequests && (
              <ScopeSummaryCard
                title="Special Requests"
                subtitle="Extra service requests"
                value={`${specialRequests.length} requests`}
                onEdit={() => editScopeStep(ScopeOfWorkStep.SpecialRequests)}
              />
            )}
          </Box>
        </Card>

        {/* Location Display (Read-only) */}
        {job?.coverageArea?.address && (
          <Card sx={{ border: "none", mb: 2 }}>
            <EditableSectionHeader title="Location" disabled={true} />
            <Box
              sx={{
                p: 1.25,
                borderRadius: 2,
                bgcolor: alphaVar(colors.orange.base, 0.04),
                border: `1px solid ${alphaVar(colors.orange.base, 0.12)}`,
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.gray?.[800] || "var(--fx-surface-text, #0F172A)", mb: 0.5 }}>Job address</Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  color: colors.gray?.[600] || "var(--fx-surface-textsecondary, #64748B)",
                  lineHeight: 1.5,
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                {job.coverageArea.address}
              </Typography>
            </Box>
          </Card>
        )}

        {/* Areas & Tasks Details */}
        <Card sx={{ border: "none", mb: 2 }}>
          <Typography sx={{ fontWeight: 700, mb: 2, fontSize: 15, color: colors.gray[800] }}>Areas & Tasks Details</Typography>

          {activeAreas.length > 0 ? (
            <Stack spacing={1.5}>
              {activeAreas.map((area, idx) => {
                const areaUid = String(area?.uid ?? idx);
                const areaQuantity = getAreaQuantity(area);
                const areaTasks = Array.isArray(area?.tasks) ? area.tasks : [];
                const areaNotesCount = areaTasks.filter((task) => String(task?.note ?? "").trim().length > 0).length;
                const isAreaOpen = expandedAreaUid === areaUid;

                return (
                  <Box
                    key={areaUid}
                    sx={{
                      borderRadius: 2,
                      border: `1px solid ${alphaVar(colors.orange.base, 0.1)}`,
                      bgcolor: "var(--fx-surface-base, #FFFFFF)",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      onClick={() => setExpandedAreaUid(isAreaOpen ? null : areaUid)}
                      sx={{
                        p: 1.25,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        cursor: "pointer",
                        bgcolor: alphaVar(colors.orange.base, 0.035),
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: 13,
                            color: colors.gray[800],
                            lineHeight: 1.25,
                          }}
                        >
                          {area?.name || "Unnamed area"}
                        </Typography>

                        <Typography sx={{ fontSize: 11, color: colors.gray[500], mt: 0.25 }}>
                          {areaQuantity > 0 ? `${areaQuantity} ${unitLabel} · ` : ""}
                          {areaTasks.length} tasks
                          {areaNotesCount > 0 ? ` · ${areaNotesCount} notes` : ""}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 1,
                          bgcolor: "var(--fx-surface-base, #FFFFFF)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: colors.orange.base,
                          flexShrink: 0,
                        }}
                      >
                        {isAreaOpen ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                      </Box>
                    </Box>

                    <Collapse in={isAreaOpen} timeout="auto" unmountOnExit>
                      <Box sx={{ px: 1.25, py: 1 }}>
                        {areaTasks.length > 0 ? (
                          <Stack spacing={0.5}>
                            {areaTasks.map((task, tIdx) => {
                              const taskUid = String(task?.uid ?? `${areaUid}-${tIdx}`);
                              const taskName = task?.name ?? "Unnamed task";
                              const note = String(task?.note ?? "").trim();
                              const hasNote = note.length > 0;
                              const isHovering = hoverTaskUid === taskUid;
                              const isNoteOpen = openNotePreviewUid === taskUid;

                              return (
                                <Box
                                  key={taskUid}
                                  sx={{
                                    px: 0.75,
                                    py: 0.35,
                                    borderRadius: 1.25,
                                    "&:hover": { bgcolor: alphaVar(colors.orange.base, 0.035) },
                                  }}
                                  onMouseEnter={() => setHoverTaskUid(taskUid)}
                                  onMouseLeave={() => setHoverTaskUid(null)}
                                >
                                  <Box
                                    sx={{
                                      minHeight: 34,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      gap: 1,
                                    }}
                                  >
                                    <Typography sx={{ fontSize: 13, color: colors.gray[700], flex: 1, minWidth: 0 }}>{taskName}</Typography>

                                    <Box
                                      sx={{
                                        width: 34,
                                        minWidth: 34,
                                        height: 30,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      {hasNote ? (
                                        <Tooltip title={isNoteOpen ? "Hide note" : "View note"} arrow placement="top">
                                          <ChatBubble
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setOpenNotePreviewUid(isNoteOpen ? null : taskUid);
                                            }}
                                            sx={{
                                              fontSize: 16,
                                              color: isNoteOpen ? colors.orange[700] : colors.orange.base,
                                              cursor: "pointer",
                                            }}
                                          />
                                        </Tooltip>
                                      ) : (
                                        <Box
                                          sx={{
                                            opacity: isHovering ? 1 : 0,
                                            pointerEvents: isHovering ? "auto" : "none",
                                            transition: "opacity 120ms ease",
                                          }}
                                        >
                                          <TaskNoteBubble
                                            areaUid={areaUid}
                                            taskId={task?.uid || String(task?.id)}
                                            taskName={taskName}
                                            noteFromStore={task?.note ?? ""}
                                            icon={<AddCircleOutline fontSize="small" />}
                                          />
                                        </Box>
                                      )}
                                    </Box>
                                  </Box>

                                  <Collapse in={hasNote && isNoteOpen} timeout="auto" unmountOnExit>
                                    <Box
                                      sx={{
                                        mt: 0.5,
                                        mb: 0.5,
                                        p: 1,
                                        borderRadius: 1.5,
                                        bgcolor: alphaVar(colors.orange.base, 0.055),
                                        border: `1px solid ${alphaVar(colors.orange.base, 0.14)}`,
                                        display: "flex",
                                        alignItems: "flex-start",
                                        justifyContent: "space-between",
                                        gap: 1,
                                        minWidth: 0,
                                        overflow: "hidden",
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: 12,
                                          color: colors.gray[600],
                                          lineHeight: 1.5,
                                          whiteSpace: "pre-wrap",
                                          overflowWrap: "anywhere",
                                          wordBreak: "break-word",
                                          flex: 1,
                                          minWidth: 0,
                                        }}
                                      >
                                        {note}
                                      </Typography>

                                      <Box
                                        sx={{
                                          width: 30,
                                          minWidth: 30,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          mt: -0.5,
                                          flexShrink: 0,
                                        }}
                                      >
                                        <TaskNoteBubble
                                          areaUid={areaUid}
                                          taskId={task.uid}
                                          taskName={taskName}
                                          noteFromStore={task?.note ?? ""}
                                          icon={<Edit fontSize="small" />}
                                        />
                                      </Box>
                                    </Box>
                                  </Collapse>
                                </Box>
                              );
                            })}
                          </Stack>
                        ) : (
                          <Typography sx={{ fontSize: 13, color: colors.gray[500] }}>No tasks assigned.</Typography>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}
            </Stack>
          ) : (
            <Typography sx={{ fontSize: 13, color: colors.gray[500] }}>No areas assigned.</Typography>
          )}
        </Card>

        {/* Objects */}
        {selectedObjects.length > 0 && (
          <Card sx={{ border: "none", mb: 2 }}>
            <Typography sx={{ fontWeight: 700, mb: 1.5, fontSize: 15, color: colors.gray[800] }}>Objects</Typography>
            <ObjectGrid items={selectedObjects} fallbackAlt="Object" fallbackName="Unnamed object" />
          </Card>
        )}

        {/* Special Requests */}
        {shouldShowSpecialRequests && specialRequests.length > 0 && (
          <Card sx={{ border: "none", mb: 2 }}>
            <Typography sx={{ fontWeight: 700, mb: 1.5, fontSize: 15, color: colors.gray[800] }}>Special Requests</Typography>
            <ObjectGrid items={specialRequests} fallbackAlt="Special request" fallbackName="Special request" />
          </Card>
        )}
      </Grid>

      {/* Pricing Column */}
      <Grid
        item
        xs={12}
        lg={4}
        sx={{
          position: { lg: "sticky" },
          top: { lg: 7 },
          alignSelf: "flex-start",
        }}
      >
        <Box
          sx={{
            borderRadius: 3,
            boxShadow: `0 12px 32px ${alphaVar(colors.orange.base, 0.08)}, 0 2px 8px rgba(0,0,0,0.04)`,
            bgcolor: "var(--fx-surface-base, #FFFFFF)",
            p: 2.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                bgcolor: alphaVar(colors.orange.base, 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"
                  stroke={colors.orange.base}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 15, color: colors.gray?.[800] || "var(--fx-surface-text, #0F172A)", lineHeight: 1.2 }}>Pricing</Typography>
              <Typography sx={{ fontSize: 12, color: colors.gray?.[400] || "var(--fx-surface-texttertiary, #94A3B8)", mt: 0.25 }}>Override the pricing for this job</Typography>
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: 12,
                    textTransform: "uppercase",
                    letterSpacing: 0.6,
                    color: colors.gray?.[500] || "var(--fx-surface-textsecondary, #64748B)",
                    mb: 0.75,
                  }}
                >
                  Pricing Type
                </Typography>
                <Select
                  size="small"
                  value={pricingBy}
                  onChange={handlePricingTypeChange}
                  sx={{
                    borderRadius: 2,
                    bgcolor: colors.gray?.[50] || "var(--fx-surface-sunken, #F8FAFC)",
                    fontSize: 13,
                    color: colors.gray?.[800] || "var(--fx-surface-text, #0F172A)",
                  }}
                >
                  {monetaryAmountOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <FormLabel
                  sx={{
                    fontWeight: 600,
                    fontSize: 12,
                    textTransform: "uppercase",
                    letterSpacing: 0.6,
                    color: colors.gray?.[500] || "var(--fx-surface-textsecondary, #64748B)",
                    mb: 0.75,
                  }}
                >
                  {showUnits ? "Unit Price" : "Amount (USD)"}
                </FormLabel>
                <TextField
                  type="number"
                  size="small"
                  value={pricingPrice || ""}
                  onChange={handlePricingPriceChange}
                  placeholder="0.00"
                  inputProps={{ min: 0, step: "0.01" }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: colors.gray?.[50] || "var(--fx-surface-sunken, #F8FAFC)",
                      fontSize: 13,
                    },
                  }}
                />
              </FormControl>
            </Grid>

            {showUnits && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <FormLabel
                    sx={{
                      fontWeight: 600,
                      fontSize: 12,
                      textTransform: "uppercase",
                      letterSpacing: 0.6,
                      color: colors.gray?.[500] || "var(--fx-surface-textsecondary, #64748B)",
                      mb: 0.75,
                    }}
                  >
                    Units / Quantity
                  </FormLabel>
                  <TextField
                    type="number"
                    size="small"
                    value={pricingUnits || ""}
                    onChange={handlePricingUnitsChange}
                    placeholder="0"
                    inputProps={{ min: 0, step: "1" }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        bgcolor: colors.gray?.[50] || "var(--fx-surface-sunken, #F8FAFC)",
                        fontSize: 13,
                      },
                    }}
                  />
                </FormControl>
              </Grid>
            )}
          </Grid>

          <Box
            sx={{
              mt: 2.5,
              p: 2,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${alphaVar(colors.orange.base, 0.07)} 0%, ${alphaVar(colors.orange.base, 0.03)} 100%)`,
              border: `1px solid ${alphaVar(colors.orange.base, 0.15)}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  color: colors.orange.base,
                  mb: 0.25,
                }}
              >
                Total Override Price
              </Typography>
              <Typography sx={{ fontSize: 13, color: colors.gray?.[600] || "var(--fx-surface-textsecondary, #64748B)" }}>
                {monetaryAmountOptions.find((o) => o.value === pricingBy)?.label || pricingBy}
              </Typography>
            </Box>

            <Box
              sx={{
                textAlign: "right",
                px: 2,
                py: 1,
                borderRadius: 2,
                bgcolor: colors.orange.base,
                boxShadow: `0 4px 12px ${alphaVar(colors.orange.base, 0.35)}`,
                flexShrink: 0,
              }}
            >
              <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#fff", lineHeight: 1 }}>{money(totalNum)}</Typography>
            </Box>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
}

// Subcomponents

const Row = ({
  label,
  value,
  fallback = "N/A",
  hideIfEmpty = false,
  textTransform = "capitalize",
}: {
  label: string;
  value: any;
  fallback?: string;
  hideIfEmpty?: boolean;
  textTransform?: string;
}) => {
  const finalValue = value ?? fallback;
  if (hideIfEmpty && !value) return null;

  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5, gap: 2 }}>
      <Typography sx={{ fontSize: 14, color: colors.gray?.[400] || "var(--fx-surface-texttertiary, #94A3B8)" }}>{label}</Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 500, textAlign: "right", textTransform }}>{finalValue}</Typography>
    </Box>
  );
};

const Card = ({ children, sx }: { children: React.ReactNode; sx?: any }) => (
  <Box
    sx={{
      p: 2,
      border: `1px solid ${colors.gray[100]}`,
      borderRadius: 2,
      bgcolor: "var(--fx-surface-base, #FFFFFF)",
      ...sx,
    }}
  >
    {children}
  </Box>
);

const EditableSectionHeader = ({ title, onEdit, disabled = false }: { title: string; onEdit?: () => void; disabled?: boolean }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1.5 }}>
    <Typography variant="h6" sx={{ fontWeight: 700, color: colors.gray?.[800] || "var(--fx-surface-text, #0F172A)", fontSize: 16 }}>
      {title}
    </Typography>
    {!disabled && onEdit && (
      <Tooltip title="Edit">
        <Box
          onClick={onEdit}
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1,
            bgcolor: alphaVar(colors.orange.base, 0.08),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: colors.orange.base,
            cursor: "pointer",
            flexShrink: 0,
            "&:hover": { bgcolor: alphaVar(colors.orange.base, 0.14) },
          }}
        >
          <Edit sx={{ fontSize: 15 }} />
        </Box>
      </Tooltip>
    )}
  </Box>
);

const ScopeSummaryCard = ({
  title,
  subtitle,
  value,
  onEdit,
  disabled = false,
}: {
  title: string;
  subtitle?: string;
  value: string;
  onEdit: () => void;
  disabled?: boolean;
}) => {
  const finalValue = value || "Not selected";

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        bgcolor: alphaVar(colors.orange.base, 0.035),
        border: `1px solid ${alphaVar(colors.orange.base, 0.1)}`,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 1.25,
        minWidth: 0,
        transition: "all 140ms ease",
        "&:hover": {
          bgcolor: alphaVar(colors.orange.base, 0.055),
          borderColor: alphaVar(colors.orange.base, 0.18),
        },
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: 0.6,
            color: colors.orange.base,
            mb: 0.35,
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 700,
            color: colors.gray?.[800] || "var(--fx-surface-text, #0F172A)",
            lineHeight: 1.35,
            overflowWrap: "anywhere",
            wordBreak: "break-word",
          }}
        >
          {finalValue}
        </Typography>

        {subtitle && <Typography sx={{ fontSize: 11, color: colors.gray?.[400] || "var(--fx-surface-texttertiary, #94A3B8)", mt: 0.35, lineHeight: 1.35 }}>{subtitle}</Typography>}
      </Box>

      {!disabled && (
        <Tooltip title="Edit">
          <Box
            onClick={onEdit}
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1.25,
              bgcolor: "var(--fx-surface-base, #FFFFFF)",
              border: `1px solid ${alphaVar(colors.orange.base, 0.16)}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: colors.orange.base,
              cursor: "pointer",
              flexShrink: 0,
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              "&:hover": { bgcolor: alphaVar(colors.orange.base, 0.1) },
            }}
          >
            <Edit sx={{ fontSize: 15 }} />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

const EditableInfoRow = ({
  label,
  value,
  fallback = "N/A",
  onEdit,
  disabled = false,
}: {
  label: string;
  value: any;
  fallback?: string;
  onEdit?: () => void;
  disabled?: boolean;
}) => {
  const finalValue = value || fallback;

  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 0.5, gap: 2 }}>
      <Typography sx={{ fontSize: 14, color: colors.gray?.[400] || "var(--fx-surface-texttertiary, #94A3B8)" }}>{label}</Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 500, textAlign: "right", textTransform: "capitalize", minWidth: 0 }}>{finalValue}</Typography>

        {!disabled && onEdit && (
          <Tooltip title="Edit">
            <Box
              onClick={onEdit}
              sx={{
                width: 24,
                height: 24,
                borderRadius: 1,
                bgcolor: alphaVar(colors.orange.base, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: colors.orange.base,
                cursor: "pointer",
                flexShrink: 0,
                "&:hover": { bgcolor: alphaVar(colors.orange.base, 0.14) },
              }}
            >
              <Edit sx={{ fontSize: 15 }} />
            </Box>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

const ServiceNameOverviewRow = ({
  value,
  isEditing,
  disabled = false,
  onEdit,
  onCancel,
  onChange,
}: {
  value: string;
  isEditing: boolean;
  disabled?: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onChange: (val: string) => void;
}) => {
  const displayValue = value?.trim();

  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 0.5, gap: 2 }}>
      <Typography sx={{ fontSize: 14, color: colors.gray?.[400] || "var(--fx-surface-texttertiary, #94A3B8)" }}>Job alias</Typography>

      {isEditing ? (
        <TextField
          autoFocus
          size="small"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onCancel}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === "Escape") {
              event.preventDefault();
              onCancel();
            }
          }}
          sx={{
            maxWidth: 260,
            "& .MuiOutlinedInput-root": {
              height: 34,
              borderRadius: 1.5,
              bgcolor: "var(--fx-surface-base, #FFFFFF)",
              fontSize: 14,
              "& fieldset": { borderColor: alphaVar(colors.orange.base, 0.25) },
              "&:hover fieldset": { borderColor: colors.orange[400] },
              "&.Mui-focused fieldset": { borderColor: colors.orange.base, borderWidth: 1.5 },
            },
          }}
        />
      ) : (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: displayValue ? 600 : 400,
              textAlign: "right",
              color: displayValue ? colors.gray?.[800] || "var(--fx-surface-text, #0F172A)" : colors.gray?.[400] || "var(--fx-surface-texttertiary, #94A3B8)",
              fontStyle: displayValue ? "normal" : "italic",
            }}
          >
            {displayValue || "No alias set"}
          </Typography>

          {!disabled && (
            <Tooltip title={displayValue ? "Edit job alias" : "Add job alias"}>
              <IconButton
                onClick={onEdit}
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: 1,
                  bgcolor: alphaVar(colors.orange.base, 0.08),
                  color: colors.orange.base,
                  flexShrink: 0,
                  "&:hover": { bgcolor: alphaVar(colors.orange.base, 0.14) },
                }}
              >
                {displayValue ? <Edit sx={{ fontSize: 15 }} /> : <AddCircleOutline sx={{ fontSize: 15 }} />}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
};

const FrequencyOverviewRow = ({
  value,
  isEditing,
  disabled = false,
  onEdit,
  onCancel,
  onChange,
}: {
  value: string;
  isEditing: boolean;
  disabled?: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onChange: (val: string) => void;
}) => {
  const displayLabel = getJobFrequencyLabel(value);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 0.5,
        gap: 2,
      }}
    >
      <Typography
        sx={{
          fontSize: 14,
          textTransform: "capitalize",
          color: colors.gray?.[400] || "var(--fx-surface-texttertiary, #94A3B8)",
        }}
      >
        Frequency
      </Typography>

      {isEditing ? (
        <Select
          autoFocus
          size="small"
          value={value || EFrequencyType.WEEKLY}
          onChange={(event: any) => {
            onChange(event.target.value);
            onCancel();
          }}
          onClose={onCancel}
          sx={{
            minWidth: 160,
            fontSize: 13,
            fontWeight: 600,
            height: 32,
            backgroundColor: "var(--fx-surface-base, #FFFFFF)",
          }}
        >
          {JOB_FREQUENCY_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13, fontWeight: 500 }}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      ) : (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: colors.gray?.[800] || "var(--fx-surface-text, #0F172A)",
              textAlign: "right",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {displayLabel}
          </Typography>

          {!disabled && (
            <Tooltip title="Edit frequency">
              <Box
                onClick={onEdit}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 24,
                  height: 24,
                  borderRadius: 1,
                  cursor: "pointer",
                  color: colors.gray?.[400] || "var(--fx-surface-texttertiary, #94A3B8)",
                  "&:hover": {
                    bgcolor: alphaVar(colors.orange?.base || "#f97316", 0.1),
                    color: colors.orange?.base || "#f97316",
                  },
                }}
              >
                <Edit sx={{ fontSize: 15 }} />
              </Box>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
};

const ObjectGrid = ({ items, fallbackAlt, fallbackName }: { items: any[]; fallbackAlt: string; fallbackName: string }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "repeat(2, 1fr)",
        sm: "repeat(3, 1fr)",
        md: "repeat(4, 1fr)",
        lg: "repeat(5, 1fr)",
      },
      gap: 1.5,
    }}
  >
    {items.map((item) => (
      <Box
        key={item?.uid}
        sx={{
          height: 120,
          borderRadius: 2,
          bgcolor: "var(--fx-surface-base, #FFFFFF)",
          border: `1px solid ${alphaVar(colors.orange.base, 0.12)}`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 1,
          overflow: "hidden",
        }}
      >
        <Box sx={{ width: 64, height: 58, display: "flex", alignItems: "center", justifyContent: "center", mb: 1 }}>
          {item?.imageUrl ? (
            <Box component="img" src={item.imageUrl} alt={item?.name || fallbackAlt} sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
          ) : (
            <Typography sx={{ fontSize: 20, fontWeight: 800, color: colors.orange.base }}>{String(item?.name || "?").charAt(0)}</Typography>
          )}
        </Box>

        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            color: colors.gray[700],
            textAlign: "center",
            lineHeight: 1.25,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {item?.name || fallbackName}
        </Typography>
      </Box>
    ))}
  </Box>
);

const DescriptionSection = ({
  descriptionJob,
  isEditingDescription,
  setIsEditingDescription,
  onChangeDescription,
}: {
  descriptionJob: string;
  isEditingDescription: boolean;
  setIsEditingDescription: (val: boolean) => void;
  onChangeDescription: (val: string) => void;
}) => (
  <Box sx={{ pt: 1.25, borderTop: `1px solid ${colors.gray?.[100] || "var(--fx-surface-border, #E2E8F0)"}` }}>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: colors.gray?.[800] || "var(--fx-surface-text, #0F172A)" }}>Description</Typography>

      {!isEditingDescription && (
        <Tooltip title={descriptionJob.trim() ? "Edit description" : "Add description"}>
          <IconButton
            onClick={() => setIsEditingDescription(true)}
            sx={{
              width: 24,
              height: 24,
              borderRadius: 1,
              bgcolor: alphaVar(colors.orange.base, 0.08),
              color: colors.orange.base,
              flexShrink: 0,
              "&:hover": { bgcolor: alphaVar(colors.orange.base, 0.14) },
            }}
          >
            {descriptionJob.trim() ? <Edit sx={{ fontSize: 15 }} /> : <AddCircleOutline sx={{ fontSize: 15 }} />}
          </IconButton>
        </Tooltip>
      )}
    </Box>

    {isEditingDescription ? (
      <TextField
        autoFocus
        fullWidth
        multiline
        minRows={3}
        maxRows={7}
        size="small"
        placeholder="Write a short description for this job"
        value={descriptionJob}
        onChange={(event) => onChangeDescription(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            setIsEditingDescription(false);
          }
          if (event.key === "Escape") {
            event.preventDefault();
            setIsEditingDescription(false);
          }
        }}
        onBlur={() => setIsEditingDescription(false)}
        sx={{
          "& .MuiOutlinedInput-root": {
            alignItems: "flex-start",
            borderRadius: 2,
            bgcolor: "var(--fx-surface-base, #FFFFFF)",
            fontSize: 14,
            "& fieldset": { borderColor: "rgba(249, 115, 22, 0.25)" },
            "&:hover fieldset": { borderColor: colors.orange[400] },
            "&.Mui-focused fieldset": { borderColor: colors.orange.base, borderWidth: 1.5 },
          },
        }}
      />
    ) : descriptionJob.trim() ? (
      <Typography sx={{ fontSize: 13, color: colors.gray[700], lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{descriptionJob}</Typography>
    ) : (
      <Typography sx={{ fontSize: 13, color: colors.gray[400], fontStyle: "italic" }}>No description for this job.</Typography>
    )}
  </Box>
);
